import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BaseApiService } from '../../core/services/base-api.service';
import { ApiResponse } from '../../shared/models/api-response.model';
import { Library } from '../../types/library';
import { BrowseSearchQuery, BrowseIndexError, FilterOption } from '../../features/browse-index/models';

@Injectable({
  providedIn: 'root',
})
export class LibrariesService extends BaseApiService {
  protected override basePath = '/api/v1';

  getLibraries(query: BrowseSearchQuery): Observable<ApiResponse<Library[]>> {
    const params = this.buildQueryParams(query);
    const headers = { 'x-koha-embed': 'library_hours' };
    return this.getWithCount<Library[]>('/libraries', params, headers).pipe(
      catchError(error => this.handleError(error))
    );
  }

  fetchFacetOptions(groupKey: string, searchTerm: string, page: number, pageSize: number): Observable<{ options: FilterOption[], totalCount: number }> {
    // Fetch all libraries (up to 500) for client-side aggregation of field values
    let params = new HttpParams().set('_page', '1').set('_per_page', '500');

    if (searchTerm) {
      params = params.set('q', JSON.stringify({ [groupKey]: { '-like': `%${searchTerm}%` } }));
    }

    // Use getWithCount to properly read both body and X-Total-Count header
    return this.getWithCount<Library[]>('/libraries', params).pipe(
      map(response => {
        const libraries = response.data || [];
        const extractedArray = libraries
          .map((lib: any) => lib[groupKey])
          .filter((val: any) => val !== null && val !== undefined && String(val).trim() !== '');

        const uniqueValues = [...new Set(
          extractedArray
            .map((val: any) => String(val).trim())
            .filter((val: string) => val.length > 0)
        )].sort() as string[];

        return {
          options: uniqueValues.map(val => ({
            value: val,
            label: val,
          })),
          totalCount: uniqueValues.length
        };
      }),
      catchError(() => of({ options: [], totalCount: 0 }))
    );
  }

  private buildQueryParams(query: BrowseSearchQuery): HttpParams {
    let params = new HttpParams()
      .set('_page', query.page.toString())
      .set('_per_page', query.pageSize.toString());

    const kohaQueryObject: Record<string, any> = {};
    let hasConditions = false;

    if (query.search && query.search.trim().length > 0) {
      kohaQueryObject['name'] = { '-like': `%${query.search.trim()}%` };
      hasConditions = true;
    }

    for (const filter of query.filters) {
      if (filter.values.length === 1) {
        kohaQueryObject[filter.field] = filter.values[0];
        hasConditions = true;
      } else if (filter.values.length > 1) {
        kohaQueryObject[filter.field] = filter.values;
        hasConditions = true;
      }
    }

    if (hasConditions) {
      params = params.set('q', JSON.stringify(kohaQueryObject));
    }

    if (query.sort && query.sort.field && query.sort.field !== 'relevance') {
      const prefix = query.sort.direction === 'asc' ? '+' : '-';
      params = params.set('_order_by', `${prefix}${query.sort.field}`);
    }

    return params;
  }

  private handleError(error: any): Observable<never> {
    const status: number = error.status ?? 0;
    let userMessage: string;

    switch (status) {
      case 0: userMessage = 'ERRORS.NETWORK_UNAVAILABLE'; break;
      case 401: userMessage = 'ERRORS.UNAUTHORIZED'; break;
      case 403: userMessage = 'ERRORS.FORBIDDEN'; break;
      case 404: userMessage = 'ERRORS.NOT_FOUND'; break;
      case 500: case 502: case 503: userMessage = 'ERRORS.SERVER_ERROR'; break;
      default: userMessage = 'ERRORS.UNKNOWN';
    }

    return throwError(() => ({
      status,
      message: userMessage,
      technical: error.error || error.message || 'Unknown error',
      retryable: status === 0 || status >= 500,
    } as BrowseIndexError));
  }
}
